import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Bell, AlertTriangle, AlertCircle, CheckCircle, ChevronRight, 
  Filter, RefreshCw, Mail, Eye, CheckCheck, MapPin, Calendar, User
} from 'lucide-react';
import Navbar from '@/components/Navbar';
import { api } from '@/utils/api';
import { useLanguage } from '@/LanguageContext';

const RiskNotifications = ({ user, onLogout }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [unreadCount, setUnreadCount] = useState(0);
  const [summary, setSummary] = useState({ high_risk: 0, moderate_risk: 0, total_unread: 0 });
  
  // Filters
  const [filterRiskLevel, setFilterRiskLevel] = useState('');
  const [filterReadStatus, setFilterReadStatus] = useState('');

  useEffect(() => {
    fetchNotifications();
    fetchSummary();
  }, [page, filterRiskLevel, filterReadStatus]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const isRead = filterReadStatus === 'read' ? true : filterReadStatus === 'unread' ? false : null;
      const res = await api.getRiskNotifications(page, 15, filterRiskLevel || null, isRead);
      setNotifications(res.data.notifications);
      setUnreadCount(res.data.unread_count);
      setTotalPages(Math.ceil(res.data.total / 15));
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const res = await api.getRiskNotificationSummary();
      setSummary(res.data);
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchNotifications();
    await fetchSummary();
    setRefreshing(false);
  };

  const handleMarkRead = async (notificationId) => {
    try {
      await api.markNotificationRead(notificationId);
      setNotifications(prev => prev.map(n => 
        n.id === notificationId ? { ...n, is_read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
      fetchSummary();
    } catch (error) {
      console.error('Error marking notification read:', error);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await api.markAllNotificationsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
      fetchSummary();
    } catch (error) {
      console.error('Error marking all notifications read:', error);
    }
  };

  const handleViewChild = (childId, notificationId, isRead) => {
    if (!isRead) {
      handleMarkRead(notificationId);
    }
    navigate(`/child/${childId}`);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-IN', { month: 'short', day: 'numeric' });
  };

  return (
    <>
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="risk-notifications-page">
      <Navbar user={user} onLogout={onLogout} />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between">
          <div className="flex items-center space-x-4">
            <div className="bg-gradient-to-br from-[#EF4444] to-[#F59E0B] p-3 rounded-2xl shadow-lg">
              <Bell className="text-white" size={32} />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-[#111827] font-['Nunito']" data-testid="page-title">
                Risk Notifications
              </h1>
              <p className="text-base text-[#6B7280]">
                High and moderate risk alerts requiring your attention
              </p>
            </div>
          </div>
          <div className="mt-4 md:mt-0 flex gap-3">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center space-x-2 px-4 py-2 bg-white text-[#0F4C81] rounded-xl font-semibold border-2 border-[#0F4C81] hover:bg-[#0F4C81] hover:text-white transition-colors"
              data-testid="refresh-button"
            >
              <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
              <span>Refresh</span>
            </button>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="flex items-center space-x-2 px-4 py-2 bg-[#10B981] text-white rounded-xl font-semibold hover:bg-[#059669] transition-colors"
                data-testid="mark-all-read-button"
              >
                <CheckCheck size={18} />
                <span>Mark All Read</span>
              </button>
            )}
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-red-50 rounded-xl p-5 border-2 border-red-200" data-testid="high-risk-summary">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-red-600 uppercase">High Risk</p>
                <p className="text-3xl font-bold text-red-700">{summary.high_risk}</p>
              </div>
              <AlertTriangle className="text-red-500" size={40} />
            </div>
          </div>
          <div className="bg-yellow-50 rounded-xl p-5 border-2 border-yellow-200" data-testid="moderate-risk-summary">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-yellow-600 uppercase">Moderate Risk</p>
                <p className="text-3xl font-bold text-yellow-700">{summary.moderate_risk}</p>
              </div>
              <AlertCircle className="text-yellow-500" size={40} />
            </div>
          </div>
          <div className="bg-blue-50 rounded-xl p-5 border-2 border-blue-200" data-testid="total-unread-summary">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-blue-600 uppercase">Total Unread</p>
                <p className="text-3xl font-bold text-blue-700">{summary.total_unread}</p>
              </div>
              <Bell className="text-blue-500" size={40} />
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl p-4 border-2 border-slate-100 mb-6 flex flex-wrap gap-4 items-center">
          <Filter size={20} className="text-[#6B7280]" />
          <select
            value={filterRiskLevel}
            onChange={(e) => { setFilterRiskLevel(e.target.value); setPage(1); }}
            className="px-4 py-2 border-2 border-slate-200 rounded-lg focus:border-[#0F4C81] focus:outline-none"
            data-testid="filter-risk-level"
          >
            <option value="">All Risk Levels</option>
            <option value="high">High Risk Only</option>
            <option value="moderate">Moderate Risk Only</option>
          </select>
          <select
            value={filterReadStatus}
            onChange={(e) => { setFilterReadStatus(e.target.value); setPage(1); }}
            className="px-4 py-2 border-2 border-slate-200 rounded-lg focus:border-[#0F4C81] focus:outline-none"
            data-testid="filter-read-status"
          >
            <option value="">All Notifications</option>
            <option value="unread">Unread Only</option>
            <option value="read">Read Only</option>
          </select>
        </div>

        {/* Notifications List */}
        <div className="space-y-4">
          {loading ? (
            <div className="text-center py-12">
              <RefreshCw size={32} className="mx-auto mb-4 animate-spin text-[#0F4C81]" />
              <p className="text-[#6B7280]">Loading notifications...</p>
            </div>
          ) : notifications.length === 0 ? (
            <div className="bg-white rounded-xl p-12 text-center border-2 border-slate-100">
              <CheckCircle size={64} className="mx-auto mb-4 text-[#10B981]" />
              <h3 className="text-xl font-bold text-[#111827] mb-2">All Caught Up!</h3>
              <p className="text-[#6B7280]">No risk notifications to display.</p>
            </div>
          ) : (
            notifications.map((notification) => (
              <div
                key={notification.id}
                className={`bg-white rounded-xl border-2 transition-all hover:shadow-md cursor-pointer ${
                  notification.is_read 
                    ? 'border-slate-100' 
                    : notification.risk_level === 'high' 
                      ? 'border-red-300 bg-red-50/50' 
                      : 'border-yellow-300 bg-yellow-50/50'
                }`}
                onClick={() => handleViewChild(notification.child_id, notification.id, notification.is_read)}
                data-testid={`notification-${notification.id}`}
              >
                <div className="p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4">
                      {/* Risk Icon */}
                      <div className={`p-2 rounded-full ${
                        notification.risk_level === 'high' 
                          ? 'bg-red-100' 
                          : 'bg-yellow-100'
                      }`}>
                        {notification.risk_level === 'high' ? (
                          <AlertTriangle className="text-red-600" size={24} />
                        ) : (
                          <AlertCircle className="text-yellow-600" size={24} />
                        )}
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <h3 className="font-bold text-[#111827] text-lg">
                            {notification.child_name}
                          </h3>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                            notification.risk_level === 'high'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {notification.risk_level.toUpperCase()} RISK
                          </span>
                          {!notification.is_read && (
                            <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                          )}
                        </div>
                        
                        <div className="flex flex-wrap gap-3 text-sm text-[#6B7280] mb-2">
                          <span className="flex items-center">
                            <User size={14} className="mr-1" />
                            {notification.child_age_months} months
                          </span>
                          <span className="flex items-center">
                            <MapPin size={14} className="mr-1" />
                            {notification.district || 'N/A'}
                            {notification.taluk && ` • ${notification.taluk}`}
                          </span>
                          <span className="flex items-center">
                            <Calendar size={14} className="mr-1" />
                            {formatDate(notification.screening_date)}
                          </span>
                        </div>
                        
                        <p className="text-sm text-[#4B5563]">
                          Screened by <span className="font-semibold">{notification.screener_name}</span>
                        </p>
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center space-x-2">
                      {notification.email_sent && (
                        <span className="text-green-500" title="Email sent">
                          <Mail size={18} />
                        </span>
                      )}
                      {!notification.is_read && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMarkRead(notification.id);
                          }}
                          className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                          title="Mark as read"
                          data-testid={`mark-read-${notification.id}`}
                        >
                          <Eye size={18} className="text-[#6B7280]" />
                        </button>
                      )}
                      <ChevronRight size={20} className="text-[#9CA3AF]" />
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center space-x-4 mt-8">
            <button
              onClick={() => setPage(prev => Math.max(1, prev - 1))}
              disabled={page === 1}
              className="px-4 py-2 bg-white border-2 border-slate-200 rounded-lg font-semibold disabled:opacity-50 hover:border-[#0F4C81] transition-colors"
              data-testid="prev-page"
            >
              Previous
            </button>
            <span className="text-[#6B7280]">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage(prev => Math.min(totalPages, prev + 1))}
              disabled={page === totalPages}
              className="px-4 py-2 bg-white border-2 border-slate-200 rounded-lg font-semibold disabled:opacity-50 hover:border-[#0F4C81] transition-colors"
              data-testid="next-page"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
    
    {/* Footer */}
    <footer className="bg-[#F3F4F6] border-t border-gray-200 py-3 text-center">
      <p className="text-sm text-[#6B7280]">© 2025 Hamrith Consulting Services LLP (HCS). All Rights Reserved.</p>
    </footer>
    </>
  );
};

export default RiskNotifications;
